import React, { useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationTriangleIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import type { PositionDetail } from '@/types';
import { Button } from '@/components/ui/Button';
import { DecimalInput } from '@/components/ui/DecimalInput';
import { SignedValue } from '@/components/ui/SignedValue';
import { useToastStore } from '@/stores';
import { getErrorMessage } from '@/utils/get-error-message';
import { formatDecimalString, toDecimalString } from '@/utils/decimal-display';
import { formatDate } from '@/utils/format';
import { effectiveRates } from '@/services/trades/effective-rate';
// Specific module, NOT the hooks barrel — keeps modal unit tests mockable (mirrors AmendTradeModal).
import { canPerform } from '@/features/trades/hooks/trade-actions';
import type { useReRate } from '@/hooks/trades';
import {
  breachesFeeFloor,
  buildReRatePayload,
  hasRateChange,
  reRateEditableFields,
  reRateMode,
  warnsLowResultingRate,
  LOW_RATE_WARN_PCT,
  type ReRateFormValues,
} from '@/services/trades/rerate-mapper';
import { useReRatePreview } from '../../../hooks/useReRatePreview';

const { Panel: DialogPanel, Title: DialogTitle } = Dialog;
const { Child: TransitionChild } = Transition;

interface ReRateModalProps {
  isOpen: boolean;
  onClose: () => void;
  trade: PositionDetail;
  /** The `useReRate` mutation handle, owned by the hosting page. */
  reRate: ReturnType<typeof useReRate>;
}

const num = (v: unknown): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
};

/** Re-rate's effective-date window is validated by the backend in UTC
 *  (PositionReRateSerializer uses django_timezone.now().date(); TIME_ZONE='UTC'),
 *  UNLIKE the local-zone lifecycle modals (Settlement/Notice use todayLocalKey()
 *  for legal-clock reasons and carry no UTC server guard). Deriving the boundary
 *  from the UTC date keeps the picker, the default, and the backdated warning in
 *  lockstep with the serializer — no month/day-boundary dead default or silently
 *  suppressed "accruals will be recalculated" note (LR-528 self-review R2). */
const todayUtcKey = (): string => new Date().toISOString().slice(0, 10);

/** Pure calendar arithmetic on a Y-M-D key — reads back the same components it
 *  writes, so it is timezone-independent (safe on the UTC key above). */
const addDays = (iso: string, days: number): string => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d + days);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
};

const rateStr = (r: number | undefined): string =>
  r === undefined || !Number.isFinite(r) ? '—' : `${formatDecimalString(r, { dp: 2 })}%`;

// Single Trade View block system (mirrors KpiStrip tokens).
const BLOCK = 'overflow-hidden rounded-[10px] border border-lr-border bg-lr-surface';
const BLOCK_HEADER =
  'border-b border-lr-border bg-lr-bg px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.6px] text-lr-muted';
const TILE_LABEL = 'text-[11px] font-semibold uppercase tracking-[0.6px] text-lr-muted';
const FIELD_LABEL = 'mb-1 block text-[11px] font-medium uppercase tracking-wide text-lr-muted';
const INPUT =
  'w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500';
// Stable ids so the hints can be wired to their inputs via aria-describedby —
// a colour-only signal does not exist for a screen reader.
const LOW_RATE_NOTE_ID = 'rerate-low-rate-note';
const FEE_FLOOR_ERROR_ID = 'rerate-fee-floor-error';
const PILL =
  'inline-flex items-center gap-1 rounded-full border border-lr-border bg-lr-bg px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.5px] text-lr-muted';

/** One "label / value" tile in the Single Trade View aesthetic (KpiMetric-lite). */
function Tile({
  label,
  sub,
  children,
}: {
  label: React.ReactNode;
  sub?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0 px-4 py-3">
      <div className={TILE_LABEL}>{label}</div>
      <div className="mt-2 flex items-baseline whitespace-nowrap">{children}</div>
      {sub != null && sub !== '' && (
        <div className="mt-1 whitespace-nowrap text-[11.5px] text-lr-faint">{sub}</div>
      )}
    </div>
  );
}

/**
 * LR-528: re-rate an ACTIVE (or backdated MATURED) position — "from this date the
 * rate is now X". A purpose-built, block-styled modal (Single Trade View tiles,
 * not the reused trade-entry form): a CURRENT block, the editable NEW RATE for the
 * booked mode (rebate → benchmark/spread, fee → fee%; the mode itself is locked),
 * an effective-date picker with a backdated warning, and a PREVIEW of rate + daily
 * PnL before→after. Confirm POSTs the diff; server 400/409 surface inline.
 */
export function ReRateModal({ isOpen, onClose, trade, reRate }: ReRateModalProps) {
  const { mutate, isPending, error, reset } = reRate;
  const { addToast } = useToastStore();

  const mode = reRateMode(trade);
  // R7: a fixed rebate trade exposes only benchmark_rate — a spread would flip
  // calculate_total_rate to the floating branch (a forbidden rate_type change).
  const editableFields = reRateEditableFields(trade);
  const showSpread = editableFields.includes('spread_bps');
  // LR-628: a floating rebate no longer offers its benchmark for editing — it is
  // resolved from the day's fixing every night. It is still SHOWN, read-only:
  // the spread the trader is about to move sits on top of it, and hiding it
  // would leave "+40 bps" floating over nothing.
  const showBenchmarkInput = editableFields.includes('benchmark_rate');
  const BenchmarkLabel = showBenchmarkInput ? 'label' : 'span';
  // A fixed rebate keeps its rate in benchmark_rate while benchmark_name stays at
  // the model default 'sofr' — so the floating "· SOFR" suffix would mislabel it.
  // Derived from showSpread rather than re-testing rate_type: within rebate mode
  // the allow-list withholds the spread on exactly the fixed rows, so one source
  // keeps the label and the rendered fields from drifting apart.
  const isFixedRebate = mode === 'rebate' && !showSpread;
  const { benchmarkRate: liveBenchmark, showingLiveRate } = effectiveRates(trade);
  const isMatured = trade.status === 'matured';
  const today = todayUtcKey();
  const maturity = trade.maturity_date || null;

  // Effective-date window mirrors the LR-527 server guard:
  //  - min = 1st of the current calendar month (backdated depth cap).
  //  - active: future ok; a term trade cannot be re-rated past its maturity.
  //  - matured: backdated-only — strictly before today AND before maturity.
  const firstOfMonth = `${today.slice(0, 7)}-01`;
  const maturedMax = (() => {
    const yesterday = addDays(today, -1);
    const preMaturity = maturity ? addDays(maturity, -1) : yesterday;
    return preMaturity < yesterday ? preMaturity : yesterday;
  })();
  const minDate = firstOfMonth;
  const maxDate = isMatured ? maturedMax : maturity || undefined;
  const defaultEffective = isMatured ? maturedMax : today;
  // A matured trade whose maturity is in a prior calendar month has no re-rateable
  // day (backdated is capped to this month's 1st, yet must be < maturity) → the
  // picker window is empty. `re_rate` is still offered on every matured status
  // (getValidActions is date-blind by design), so gate the submit here instead of
  // firing a guaranteed-400 POST. LR-528 self-review R2.
  const emptyWindow = maxDate !== undefined && maxDate < minDate;

  const [effectiveDate, setEffectiveDate] = useState(defaultEffective);
  // LR-628: only seeded when there is an INPUT to seed. This value is what
  // `useReRatePreview` prices the "after" leg on while it prices "before" on the
  // effective figure, so holding a booked number here on a fixing-driven trade
  // would make an untouched form preview a rate jump the size of the whole market
  // move — and inflate the Δ P&L of a real spread change by the same amount.
  // Leaving it undefined lets the hook take BOTH legs from the same render, which
  // also keeps them together if a refetch lands a new fixing while the modal is
  // open. Where the input IS shown, the effective figure equals the booked one by
  // construction (a resolved `effective_*` is exactly what hides the input).
  const [benchmarkRate, setBenchmarkRate] = useState<number | undefined>(
    () => (showBenchmarkInput ? num(trade.benchmark_rate) : undefined),
  );
  const [spreadBps, setSpreadBps] = useState<number | undefined>(() => num(trade.spread_bps));
  const [feePercent, setFeePercent] = useState<number | undefined>(() => num(trade.fee_percent));

  // Reseed strictly on the closed→open transition so a background refetch while
  // open never clobbers an in-progress edit (mirrors the sibling modals).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setEffectiveDate(defaultEffective);
      setBenchmarkRate(showBenchmarkInput ? num(trade.benchmark_rate) : undefined);
      setSpreadBps(num(trade.spread_bps));
      setFeePercent(num(trade.fee_percent));
      reset();
    }
    wasOpen.current = isOpen;
  }, [isOpen, trade, showBenchmarkInput, defaultEffective, reset]);

  const preview = useReRatePreview(trade, { benchmarkRate, spreadBps, feePercent });

  const values: ReRateFormValues = {
    effective_date: effectiveDate,
    benchmark_rate: benchmarkRate,
    spread_bps: spreadBps,
    fee_percent: feePercent,
  };
  const payload = buildReRatePayload(trade, values);
  const hasChange = hasRateChange(payload);
  // Soft typo guard on the rate actually being SUBMITTED — the resolved total
  // (benchmark + spread/100), not the benchmark alone. On a floating rebate the
  // spread is where a fat-finger costs most: it is unbounded up to the ±1 000 000
  // bps serializer band, so -100000 bps takes a 6.30 % trade to -994.70 % and the
  // POST succeeds. Anchoring on the benchmark would warn about -10.5 % and stay
  // silent on that. Trade entry never had to cover this — its own -10 % warning
  // sits on the rebate-FIXED rate, the only rate it lets you hand-edit.
  // Gated on hasChange so an already-negative booked trade does not open
  // pre-warned about a rate nobody touched. The rebate-only rule now lives in
  // warnsLowResultingRate, shared with the bulk grid. Non-blocking.
  const lowRateWarning = hasChange && warnsLowResultingRate(trade, preview.newRate);
  // Fee mode has a hard floor, not a warning: the serializer rejects <= 0 outright
  // ("Fee is required for Fee mode" / min_value=0), so submitting is a guaranteed
  // 400. Gate it here, the same rule the bulk grid pre-flights.
  const feeFloor = breachesFeeFloor(trade, values);
  // Submit-path guard (ACTN-02): re-check validity against the freshly-loaded trade
  // so a stale/keyboard/race submit no-ops instead of firing a guaranteed 409.
  const canReRate = canPerform(trade, 're_rate');
  const isBackdated = !!effectiveDate && effectiveDate < today;
  // `<input type="date">` enforces min/max only on the spinner, not on typed
  // entry, and the matured-prior-month default sits below min — so re-check the
  // range here (the backend is authoritative, but do not fire a known-bad POST).
  const dateInRange =
    !!effectiveDate && effectiveDate >= minDate && (maxDate === undefined || effectiveDate <= maxDate);
  const canSubmit = canReRate && hasChange && dateInRange && !feeFloor;

  const handleConfirm = () => {
    if (!canSubmit) return;
    mutate(
      { id: trade.id, payload },
      {
        onSuccess: () => {
          addToast('success', `Trade #${trade.id} re-rated`);
          onClose();
        },
        // A failure surfaces ONCE, inline in the still-open modal (error band below).
      },
    );
  };

  const currency = trade.currency;
  const settlementCcy = trade.settlement_currency || undefined;
  const dailyNode = (
    security: string | number | null,
    converted: string | number | null | undefined,
  ) =>
    preview.isCrossCurrency && converted != null ? (
      <SignedValue value={converted} currency={settlementCcy} tier="section" />
    ) : (
      <SignedValue value={security} currency={currency} tier="section" />
    );

  // Immutable mode/type pill (a re-rate can never flip these) + read-only context.
  const modePill = mode === 'fee' ? 'Fee' : `Rebate · ${trade.rate_type === 'fixed' ? 'Fixed' : 'Floating'}`;
  const directionLabel =
    trade.trade_type_display ||
    (trade.trade_type ? trade.trade_type.charAt(0).toUpperCase() + trade.trade_type.slice(1) : null);
  const qtyLabel = typeof trade.quantity === 'number' ? trade.quantity.toLocaleString('en-US') : null;
  const agreementLabel = trade.agreement_display || (trade.agreement ? String(trade.agreement).toUpperCase() : null);
  const collateralLabel = trade.collateral_type === 'non_cash' ? 'Non-cash' : 'Cash';
  const contextItems = [
    directionLabel,
    qtyLabel ? `${qtyLabel} sh` : null,
    agreementLabel,
    collateralLabel,
  ].filter(Boolean) as string[];

  // Current-rate build-up, mirroring the KpiStrip Rate sub ("SOFR 5.30% + 100 bps").
  const buildup = (() => {
    // LR-628: the build-up quotes what the trade accrues on today.
    const bm = num(liveBenchmark);
    const bmName = (trade.benchmark_name || '').toUpperCase();
    if (mode === 'fee') {
      return bmName && bm != null ? `${bmName} ${formatDecimalString(bm, { dp: 2 })}% rebate` : 'fee';
    }
    if (trade.rate_type === 'fixed') return 'Fixed rate';
    const sp = num(trade.spread_bps);
    const spTerm = sp == null ? '' : sp >= 0 ? ` + ${sp} bps` : ` − ${-sp} bps`;
    return `${bmName} ${formatDecimalString(bm ?? 0, { dp: 2 })}%${spTerm}`;
  })();

  // Δ daily PnL (in the displayed currency) — the rate-effect delta from the hook
  // (calc(new) − calc(current)), so it reads 0 when nothing moved.
  const previewCcy = preview.isCrossCurrency ? settlementCcy : currency;
  const deltaDaily = preview.isCrossCurrency ? preview.deltaDailyConverted : preview.deltaDaily;

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-[10010]"
        onClose={() => {
          if (!isPending) onClose();
        }}
      >
        <TransitionChild
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 py-8">
            <TransitionChild
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-lg transform overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <div>
                    <DialogTitle className="text-lg font-semibold text-gray-900">Re-Rate</DialogTitle>
                    <p className="mt-0.5 text-xs text-gray-500">
                      #{trade.id} · {trade.symbol_id}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (!isPending) onClose();
                    }}
                    disabled={isPending}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none disabled:opacity-50"
                    aria-label="Close"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4 px-6 py-5">
                  {/* Immutable mode/type pill + read-only trade context (mockup S2). */}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className={PILL} title="Mode and rate type can't change in a re-rate">
                      <LockClosedIcon className="h-3 w-3" aria-hidden="true" />
                      {modePill}
                    </span>
                    {contextItems.length > 0 && (
                      <span className="flex flex-wrap items-center gap-x-2 text-xs text-lr-muted">
                        {contextItems.map((item, i) => (
                          <React.Fragment key={item}>
                            {i > 0 && (
                              <span aria-hidden="true" className="text-lr-faint">
                                ·
                              </span>
                            )}
                            <span>{item}</span>
                          </React.Fragment>
                        ))}
                      </span>
                    )}
                  </div>

                  {/* CURRENT */}
                  <div className={BLOCK}>
                    <div className={BLOCK_HEADER}>Current</div>
                    <div className="grid grid-cols-2 divide-x divide-lr-border">
                      <Tile label="Rate" sub={buildup}>
                        <span className="lr-num text-[22px] font-[600] leading-none tracking-[-0.3px] text-lr-accent">
                          {rateStr(preview.currentRate)}
                        </span>
                      </Tile>
                      <Tile label="Net P&L / day">
                        {dailyNode(preview.currentDaily, preview.currentDailyConverted)}
                      </Tile>
                    </div>
                  </div>

                  {/* NEW RATE */}
                  <div className={BLOCK}>
                    <div className={BLOCK_HEADER}>New rate</div>
                    <div className="space-y-3 px-4 py-3">
                      {mode === 'rebate' ? (
                        <>
                        <div className={showSpread ? 'grid grid-cols-2 gap-3' : ''}>
                          <div>
                            {/* A read-only figure has no control to label, so the
                                caption degrades to a span there — a dangling
                                <label> is announced as a broken field. `htmlFor`
                                is undefined in that branch, so React omits it
                                rather than putting an invalid attr on a span. */}
                            <BenchmarkLabel
                              htmlFor={showBenchmarkInput ? 'rerate-benchmark' : undefined}
                              className={FIELD_LABEL}
                            >
                              {isFixedRebate ? 'Fixed rebate rate (%)' : 'Benchmark rate (%)'}
                              {!isFixedRebate && trade.benchmark_name ? (
                                <span className="ml-1 normal-case text-lr-faint">
                                  · {trade.benchmark_name.toUpperCase()}
                                </span>
                              ) : null}
                            </BenchmarkLabel>
                            {showBenchmarkInput ? (
                              /* Signed: a negative rebate is legitimate (LR-365/LR-464) and
                                 booking already allows it — re-rate must too (LR-623). */
                              <DecimalInput
                                id="rerate-benchmark"
                                value={benchmarkRate}
                                onChange={setBenchmarkRate}
                                precision={4}
                                allowNegative
                                className={clsx(INPUT, lowRateWarning && 'border-yellow-500')}
                                aria-describedby={lowRateWarning ? LOW_RATE_NOTE_ID : undefined}
                                aria-label={
                                  isFixedRebate
                                    ? 'New fixed rebate rate percent'
                                    : 'New benchmark rate percent'
                                }
                              />
                            ) : (
                              <div
                                className={clsx(INPUT, 'flex items-center bg-lr-bg text-lr-muted')}
                                title="Resolved from the daily benchmark fixing — re-rate moves the spread"
                              >
                                {/* Guard the percent suffix exactly like the two
                                    sibling renderers of this field (RateWaterfall,
                                    TradeTermsTab): `formatDecimalString(null)`
                                    returns an em dash, which would compose into
                                    the malformed string "—%". */}
                                <span className="lr-num">
                                  {toDecimalString(liveBenchmark) != null
                                    ? `${formatDecimalString(liveBenchmark, { dp: 2 })}%`
                                    : '—'}
                                </span>
                                {/* LR-656: "fixing", NOT "today's fixing". The
                                    accrual resolves through a carry-forward
                                    window of up to MAX_FIXING_STALENESS_DAYS and
                                    the payload keeps no `as_of`, so nothing on
                                    this screen can support a same-day claim —
                                    the same conclusion LR-652 review reached for
                                    the bulk grid's chip (!348, wording "fixing").
                                    The provenance a trader prices against —
                                    fixing vs booking snapshot — survives; only
                                    the date claim goes. */}
                                <span className="ml-2 text-[10.5px] uppercase tracking-[0.5px]">
                                  {showingLiveRate ? 'fixing' : 'booked'}
                                </span>
                              </div>
                            )}
                          </div>
                          {showSpread && (
                            <div>
                              <label htmlFor="rerate-spread" className={FIELD_LABEL}>
                                Spread (bps)
                              </label>
                              {/* 2-dp parity with the trade-entry form's Spread field
                                  (SpreadBpsInputInner): same up-to-2-decimals input,
                                  same negative support, same "e.g." hint. The backend
                                  spread_bps is an IntegerField, so a fractional bps 400s
                                  on submit — identical behaviour to Create, by design. */}
                              <DecimalInput
                                id="rerate-spread"
                                value={spreadBps}
                                onChange={setSpreadBps}
                                precision={2}
                                allowNegative
                                placeholder="e.g., 50 or -25"
                                className={clsx(INPUT, lowRateWarning && 'border-yellow-500')}
                                aria-describedby={lowRateWarning ? LOW_RATE_NOTE_ID : undefined}
                                aria-label="New spread in bps"
                              />
                            </div>
                          )}
                        </div>
                        {/* Anchored on the resulting total, so it sits under the PAIR
                            rather than under whichever field happened to trigger it. */}
                        {lowRateWarning && (
                          <p id={LOW_RATE_NOTE_ID} className="text-xs text-yellow-600" role="status">
                            Resulting rate {rateStr(preview.newRate)} is below {LOW_RATE_WARN_PCT}%.
                            Verify before submit.
                          </p>
                        )}
                        </>
                      ) : (
                        <div>
                          <label htmlFor="rerate-fee" className={FIELD_LABEL}>
                            Fee (%)
                          </label>
                          <DecimalInput
                            id="rerate-fee"
                            value={feePercent}
                            onChange={setFeePercent}
                            precision={4}
                            className={clsx(INPUT, feeFloor && 'border-red-500')}
                            aria-invalid={feeFloor || undefined}
                            aria-describedby={feeFloor ? FEE_FLOOR_ERROR_ID : undefined}
                            aria-label="New fee percent"
                          />
                          {feeFloor && (
                            <p id={FEE_FLOOR_ERROR_ID} className="mt-1 text-xs text-red-600" role="alert">
                              Fee must stay above 0% — the server rejects a zero or negative fee.
                            </p>
                          )}
                        </div>
                      )}

                      <div>
                        <label htmlFor="rerate-effective-date" className={FIELD_LABEL}>
                          Effective date
                        </label>
                        <input
                          type="date"
                          id="rerate-effective-date"
                          value={effectiveDate}
                          min={minDate}
                          max={maxDate}
                          onChange={(e) => setEffectiveDate(e.target.value)}
                          aria-describedby="rerate-preview"
                          className={INPUT}
                        />
                      </div>

                      {emptyWindow && (
                        <div
                          className="rounded border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800"
                          role="note"
                        >
                          <div className="flex items-start gap-2">
                            <ExclamationTriangleIcon className="mt-px h-4 w-4 flex-none" aria-hidden="true" />
                            <span>
                              This trade matured before the current calendar month, so it has no re-rateable
                              period and can no longer be re-rated.
                            </span>
                          </div>
                        </div>
                      )}

                      {isBackdated && !emptyWindow && (
                        <div
                          className="rounded border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800"
                          role="note"
                          aria-live="polite"
                        >
                          <div className="flex items-start gap-2">
                            <ExclamationTriangleIcon className="mt-px h-4 w-4 flex-none" aria-hidden="true" />
                            <span>
                              Accruals from {formatDate(effectiveDate)} onward will be recalculated at the new
                              rate. This will be reflected in the monthly billing statement.
                            </span>
                          </div>
                          <span className="ml-6 mt-1.5 inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.4px] text-amber-700">
                            within current month only
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* PREVIEW */}
                  <div className={BLOCK} id="rerate-preview" aria-live="polite">
                    <div className={BLOCK_HEADER}>Preview</div>
                    <div className="grid grid-cols-2 divide-x divide-lr-border">
                      <Tile label="Rate">
                        <span className="flex items-baseline gap-2 whitespace-nowrap">
                          <span className="lr-num text-sm text-lr-muted">{rateStr(preview.currentRate)}</span>
                          <span aria-hidden="true" className="text-lr-faint">
                            →
                          </span>
                          <span className="lr-num text-[22px] font-[600] leading-none tracking-[-0.3px] text-lr-accent">
                            {rateStr(preview.newRate)}
                          </span>
                        </span>
                      </Tile>
                      <Tile
                        label="Net P&L / day"
                        sub={
                          deltaDaily !== undefined ? (
                            <span className="inline-flex items-baseline gap-1">
                              Δ <SignedValue value={deltaDaily} currency={previewCcy} unit="/day" tier="body" />
                            </span>
                          ) : undefined
                        }
                      >
                        <span className="flex items-baseline gap-2 whitespace-nowrap">
                          <SignedValue
                            value={
                              preview.isCrossCurrency && preview.currentDailyConverted != null
                                ? preview.currentDailyConverted
                                : preview.currentDaily
                            }
                            currency={preview.isCrossCurrency ? settlementCcy : currency}
                            tier="body"
                            className="text-lr-muted"
                          />
                          <span aria-hidden="true" className="text-lr-faint">
                            →
                          </span>
                          {dailyNode(preview.newDaily, preview.newDailyConverted)}
                        </span>
                      </Tile>
                    </div>
                  </div>

                  {error && (
                    <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-600" role="alert">
                      {getErrorMessage(error, 'Failed to re-rate the trade. Please try again.')}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                  <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    disabled={!canSubmit || isPending}
                    onClick={handleConfirm}
                  >
                    {isPending ? 'Re-rating…' : 'Re-Rate'}
                  </Button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
